import dayjs from 'dayjs';

import {
  BROWSER_CLOSE_BUSY,
  BROWSER_CLOSE_UNAUTHORIZED,
  BROWSER_PROTOCOL_VERSION,
  type BrowserViewport,
} from '@repo/contracts/browser-protocol';

import { mockPageHeight, renderMockPage, toBase64 } from './mock-page';
import type { BrowserTransport, TransportOptions } from './transport';

/**
 * A browser worker simulated in the tab. It speaks the exact protocol of the
 * real one — same `ready` gate,
 * same `frame`/`url`/`captured`/`stitch`/`error`/`closed` messages, same close
 * codes —
 * so the UI above it is finished, not stubbed.
 *
 * **Failure states are addressable, not random**, so every unhappy path is
 * reachable by hand and stable in tests. Put one of these in the host:
 *
 * | host contains  | simulates                                            |
 * | -------------- | ---------------------------------------------------- |
 * | `worker-down`  | the worker is unreachable — the socket never opens    |
 * | `busy`         | close 4409: every instance already holds a session    |
 * | `unauthorized` | close 4401: the session token was rejected           |
 * | `slow`         | a cold start (~9s) — exercises the boot progress UI  |
 * | `notfound`     | the page itself fails to load (session stays alive)  |
 * | `blocked`      | the worker's net-guard refuses the URL               |
 * | `crash`        | the browser dies 12s in — a mid-session `closed`     |
 *
 * Selected by VITE_BROWSER_MOCK=true (see transport.ts). Opt-in, so a missing
 * env var talks to the real worker rather than quietly showing a fake page.
 * The Playwright suite runs on it: CI has no worker container, and pointing
 * browser tests at a real site makes them depend on that site staying up.
 */

const BOOT_MS = 2600;
const SLOW_BOOT_MS = 9000;
const NAV_MS = 1400;
const FRAME_INTERVAL_MS = 900;
const CRASH_MS = 12_000;

function hostContains(url: string, needle: string): boolean {
  try {
    return new URL(url).host.includes(needle);
  } catch {
    return url.includes(needle);
  }
}

/** 1×1 grey JPEG — used only where no canvas exists (jsdom). */
const PLACEHOLDER_FRAME =
  '/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAP//////////////////////////////////////////////////////////////////////////////////////wAALCAABAAEBAREA/8QAFAABAAAAAAAAAAAAAAAAAAAACf/EABQQAQAAAAAAAAAAAAAAAAAAAAD/2gAIAQEAAD8AKp//2Q==';

export function createMockTransport({
  handlers,
}: TransportOptions): BrowserTransport {
  let viewport: BrowserViewport = { height: 800, mobile: false, width: 1280 };
  let currentUrl = '';
  let scrollY = 0;
  let visible = true;
  let closed = false;
  let ready = false;

  const timers: ReturnType<typeof setTimeout>[] = [];
  let frameTimer: ReturnType<typeof setInterval> | undefined;

  const later = (fn: () => void, ms: number) => {
    timers.push(setTimeout(fn, ms));
  };

  const emit: typeof handlers.onMessage = (message) => {
    if (!closed) handlers.onMessage(message);
  };

  const shutdown = (code: number) => {
    if (closed) return;
    closed = true;
    timers.forEach(clearTimeout);
    if (frameTimer) clearInterval(frameTimer);
    handlers.onClose({ clean: false, code });
  };

  const frame = (scale = 1) =>
    renderMockPage({
      height: viewport.height,
      mobile: viewport.mobile,
      scale,
      scrollY,
      url: currentUrl,
      width: viewport.width,
    });

  const sendFrame = () => {
    if (!visible || !currentUrl) return;
    const dataUrl = frame();
    emit({
      data: dataUrl ? toBase64(dataUrl) : PLACEHOLDER_FRAME,
      type: 'frame',
    });
  };

  // The first navigate() arrives right after `ready`; anything before that is
  // queued by the caller, exactly as with the real worker.
  const boot = (target: string) => {
    if (hostContains(target, 'worker-down')) {
      later(() => {
        handlers.onFailure('No browser instance answered.');
      }, 1800);
      return;
    }
    if (hostContains(target, 'unauthorized')) {
      later(() => {
        shutdown(BROWSER_CLOSE_UNAUTHORIZED);
      }, 1200);
      return;
    }
    if (hostContains(target, 'busy')) {
      later(() => {
        shutdown(BROWSER_CLOSE_BUSY);
      }, 1500);
      return;
    }

    later(
      () => {
        ready = true;
        const now = dayjs();
        emit({
          protocolVersion: BROWSER_PROTOCOL_VERSION,
          session: {
            createdAt: now.valueOf(),
            expiresAt: now.add(30, 'minute').valueOf(),
            framesDropped: 0,
            id: 'mock-session',
            idleFor: 0,
            url: '',
            viewers: 1,
            viewport,
            visible: true,
          },
          type: 'ready',
        });
        frameTimer = setInterval(sendFrame, FRAME_INTERVAL_MS);

        if (hostContains(target, 'crash')) {
          later(() => {
            shutdown(1006);
          }, CRASH_MS);
        }
      },
      hostContains(target, 'slow') ? SLOW_BOOT_MS : BOOT_MS,
    );
  };

  let bootTarget: string | null = null;

  return {
    close: () => {
      closed = true;
      timers.forEach(clearTimeout);
      if (frameTimer) clearInterval(frameTimer);
    },

    send: (message) => {
      if (closed) return;

      switch (message.type) {
        case 'navigate': {
          const target = message.url ?? currentUrl;

          // The session is provisioned lazily on the first navigate so the mock
          // can key its failure simulation off the URL the user actually asked
          // for. The real worker boots on connect; the UI can't tell, because
          // it waits for `ready` either way.
          if (!bootTarget) {
            bootTarget = target;
            boot(target);
            return;
          }
          if (!ready) {
            emit({ message: 'Session is not ready yet.', type: 'error' });
            return;
          }

          if (hostContains(target, 'blocked')) {
            emit({
              message: 'That address is not allowed by the browser service.',
              type: 'error',
            });
            return;
          }
          if (hostContains(target, 'notfound')) {
            later(() => {
              emit({
                message: `This site can't be reached — ${new URL(target).host} took too long to respond.`,
                type: 'error',
              });
            }, NAV_MS);
            return;
          }

          scrollY = 0;
          later(() => {
            currentUrl = target;
            emit({ type: 'url', url: target });
            sendFrame();
          }, NAV_MS);
          return;
        }

        case 'viewport': {
          viewport = {
            height: message.height,
            mobile: message.mobile ?? false,
            width: message.width,
          };
          scrollY = 0;
          emit({ type: 'viewport', viewport });
          sendFrame();
          return;
        }

        case 'scroll': {
          const max = mockPageHeight(viewport.height) - viewport.height;
          scrollY = Math.min(Math.max(0, scrollY + (message.deltaY ?? 0)), max);
          sendFrame();
          return;
        }

        case 'visible': {
          visible = message.visible;
          if (visible) sendFrame();
          return;
        }

        case 'stitch-prepare':
        case 'stitch-scroll':
        case 'stitch-finish': {
          if (!ready || !currentUrl) {
            emit({
              message: 'Nothing to stitch yet.',
              requestId: message.requestId,
              type: 'error',
            });
            return;
          }
          // The mock page scrolls the window, so the scroller's box is the
          // viewport and the picture is exactly the document.
          const pageHeight = mockPageHeight(viewport.height);
          const maxScroll = pageHeight - viewport.height;
          if (message.type === 'stitch-prepare') scrollY = 0;
          if (message.type === 'stitch-scroll') {
            scrollY = Math.min(Math.max(0, message.y ?? 0), maxScroll);
          }
          later(
            () => {
              if (message.type === 'stitch-finish') scrollY = 0;
              sendFrame();
              emit({
                requestId: message.requestId,
                state: {
                  actualY: scrollY,
                  background:
                    message.type === 'stitch-prepare' ? '#ffffff' : '',
                  containerBottom: viewport.height,
                  containerTop: 0,
                  contentHeight: pageHeight,
                  maxScroll,
                  totalHeight: pageHeight,
                  viewportHeight: viewport.height,
                  viewportWidth: viewport.width,
                },
                type: 'stitch',
              });
            },
            message.type === 'stitch-prepare' ? 900 : 300,
          );
          return;
        }

        case 'capture': {
          if (!ready || !currentUrl) {
            emit({
              message: 'Nothing to capture yet.',
              requestId: message.requestId,
              type: 'error',
            });
            return;
          }
          later(() => {
            const dpr = 2;
            const dataUrl = frame(dpr);
            const fullPage = message.fullPage ?? false;
            // A full-page shot is the *whole document*, which is what the real
            // worker returns and is the only way a caller can find out that a
            // page is too long to shoot whole. Reporting the viewport height
            // for both made the mock agree with itself and with nothing else.
            const height = fullPage
              ? mockPageHeight(viewport.height)
              : viewport.height;
            emit({
              bundle: {
                capturedAt: dayjs().valueOf(),
                dom: null,
                dpr,
                fullPage,
                image: dataUrl ? toBase64(dataUrl) : PLACEHOLDER_FRAME,
                imgH: height,
                imgW: viewport.width,
                pixelH: height * dpr,
                pixelW: viewport.width * dpr,
              },
              requestId: message.requestId,
              type: 'captured',
            });
          }, 420);
          return;
        }

        default:
          // Pointer and key events move nothing on a canvas mock; the real
          // worker replays them into Chromium. Dropping them silently is what
          // the worker does with stray input before `ready`, too.
          return;
      }
    },
  };
}
